import { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { SectionCard, StatusChip } from '@platform/ui-kit';
import { api, type AiIntegrationConfig, type AiProvider } from '../../api';
import { HelpPanel, type HelpContent } from '../portfolio_definition/HelpPanel';
import { describeError, useAsyncData } from './useAsyncData';

/**
 * Administration > Open AI Integration.
 *
 * Replaces the Integrations tab, which was an empty state saying integration settings
 * were not built. The owner's brief (2026-10-07): "replace in integrations menu With
 * AI integrations … create a screen that would be specific on AI integration with
 * either Claude or Microsoft or Google and have them as an option", with "ability to
 * put the API key stored", a radio for the application-wide choice, and per-module
 * overrides — "you could use Microsoft AI platform to run APR module but then they
 * could use a Claude AI platform to run, let's say, project management".
 *
 * THE NAME IS HIS, AND IT IS TWO WORDS. "rename AI Integration into Open AI
 * Integration", then "I believe it is called Open AI and not OpenAI" — so the menu row,
 * the page heading and the drawer read Open AI Integration, even though the screen
 * configures four providers. The COMPANY OpenAI, one of those four, keeps its own
 * one-word spelling in the provider list below: that is its actual name, and the two
 * spellings sitting near each other is deliberate rather than an oversight. The types
 * and the tables keep the generic AI-provider vocabulary, because
 * `OpenAiProviderKey = 'anthropic'` would be a lie in code. The FILE and the component
 * keep the name `OpenAiIntegrationTab` for the same reason — renaming them buys nothing
 * a user can see.
 *
 * NOTHING HERE CALLS AN AI PROVIDER. No SDK, no request, and no code path that turns a
 * stored key back into a usable credential. The fact that the configuration is stored
 * and not yet used is stated in the "How this works" drawer, where it is reference
 * material someone can consult. It is deliberately NOT a banner on the page any more:
 * the owner read that block and asked for it gone.
 *
 * THE KEY IS WRITE-ONLY. The server never returns one, so this file has nothing to
 * render: `has_api_key`, `api_key_hint` (four characters), and who set it when. The
 * key field is therefore an ENTRY field and never a display field — it starts empty
 * every time, and leaving it empty keeps whatever is stored.
 *
 * LAYOUT — the default radio and both panel headings are FIXED; only the provider list
 * and the module rows scroll, and the module table's column headings stay anchored
 * above them. The region's height is MEASURED, following UsersTab's precedent:
 * `height: 100%` does not work here because the shell's `<main>` is never bounded
 * (CLAUDE.md, "a screen header never scrolls").
 */

/**
 * "How this works" — the drawer nearly every screen in the product carries, IMPORTED
 * rather than re-implemented. The same component Project Management imports from this
 * path; a bespoke version on one admin screen would stand out immediately.
 *
 * The copy answers the four questions a client actually has here, and carries the one
 * sentence the page no longer does: that this configuration is stored and not yet used.
 * On the page that read as a disclaimer shouted at everyone; in the drawer it is a fact
 * somebody about to paste a production key can look up.
 */
const OPENAI_INTEGRATION_HELP: HelpContent = {
  title: 'Open AI Integration',
  icon: '🤖',
  purpose:
    'Chooses <strong>which AI provider each module runs on</strong>, and holds the <strong>credentials</strong> for those providers. One place for the whole install, so a provider is configured once rather than per module.',
  doHere: [
    'Fill in a provider&rsquo;s connection details and save its <strong>API key</strong>. A provider reads as <em>Configured</em> once it has both.',
    'Pick <strong>one application default</strong> with the radio buttons. Exactly one can be in force, which is why it is a radio and not a set of tick boxes.',
    'Give an individual module its own provider in <strong>Provider by module</strong> when it should not follow the default.',
    'Set a module back to <strong>Use application default</strong> to drop its override again.',
    'Remove a stored key with <strong>Remove key</strong>. Replacing one overwrites it; there is no way to look at either.',
  ],
  fits: 'Administration &rarr; Open AI Integration',
  firstTime:
    'Fill in the provider the client actually uses, save its key, and select it as the application default. Leave every module on <strong>Use application default</strong> until one of them genuinely needs a different provider.',
  sections: [
    {
      type: 'html',
      label: 'The default, and the exceptions to it',
      html: 'A module with <strong>no override follows the application default</strong> — nothing is stored for it at all, so changing the default moves every such module at once. An override pins one module to one provider, and that is what lets <strong>APR run on Microsoft Azure OpenAI while Project Execution &amp; Project Management runs on Anthropic (Claude)</strong>. The <em>In force</em> column always shows what a module would actually use.',
    },
    {
      type: 'defs',
      label: 'What each provider needs',
      items: [
        {
          term: 'Anthropic (Claude)',
          def: 'An API key, and nothing else. Claude models on Anthropic&rsquo;s own API.',
        },
        {
          term: 'Microsoft Azure OpenAI',
          def: 'A <strong>resource endpoint</strong> and a <strong>deployment name</strong>, plus a key. Azure addresses a model by the deployment the client created in their own subscription, not by a model name.',
        },
        {
          term: 'Google Vertex AI',
          def: 'A <strong>Google Cloud project ID</strong> and a <strong>region</strong>, plus a key. A Vertex endpoint is regional, so the region is part of the address.',
        },
        {
          term: 'OpenAI',
          def: 'An API key, and nothing else. GPT models on OpenAI&rsquo;s own API.',
        },
      ],
      note:
        'A provider counts as <em>Configured</em> only when every field it needs is filled AND a key is stored.',
    },
    {
      type: 'html',
      label: 'A saved key cannot be read back',
      html: 'A key is <strong>encrypted the moment it is saved</strong> (AES-256-GCM) and is never returned to the browser again — not to you, not to any other administrator, not to anyone. This screen can tell you only <strong>whether a key is set, its last four characters, and when and by whom it last changed</strong>. If a key is lost, it is re-entered from the provider&rsquo;s own console; there is nowhere in this application to look it up.',
    },
    {
      type: 'html',
      label: 'Stored, not yet used',
      html: 'This configuration is <strong>recorded and is not yet read by any module</strong> — the provider integration itself has still to be built.',
    },
  ],
};

/** Options for every provider picker on the screen, in the server's display order. */
function providerOptions(config: AiIntegrationConfig) {
  return config.providers.map((p) => ({ value: p.provider, label: p.name }));
}

function providerName(config: AiIntegrationConfig, provider: string | null): string | null {
  if (!provider) return null;
  return config.providers.find((p) => p.provider === provider)?.name ?? provider;
}

function formatWhen(value: string | null): string | null {
  return value ? new Date(value).toLocaleString() : null;
}

const TD: React.CSSProperties = {
  padding: '9px 14px',
  borderBottom: '1px solid #eef2f6',
  fontSize: 13.5,
  verticalAlign: 'middle',
};

/** Matches the Users screen's column headings, so the two tables read as one product. */
const TH: React.CSSProperties = {
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  color: '#90a4ae',
  padding: '9px 14px',
  background: '#eef2f6',
  borderBottom: '1.5px solid #e0e6ea',
  whiteSpace: 'nowrap',
};

/**
 * Sticky on the `th`, never on the `tr` — sticky on a row does nothing.
 *
 * `top: 0` and not a measured offset: the panel heading is outside the scroll box and
 * cannot move, so there is nothing above the headings to clear.
 */
const STICKY_TH: React.CSSProperties = { position: 'sticky', top: 0, zIndex: 2 };

interface ProviderPanelProps {
  provider: AiProvider;
  isDefault: boolean;
  busy: boolean;
  onSave: (update: { settings: Record<string, string>; apiKey?: string }) => Promise<void>;
  onRemoveKey: () => Promise<void>;
}

/**
 * One provider's section: its name, its own connection fields, and its key as a
 * state rather than a value.
 *
 * The draft lives here rather than in the parent so that typing in one provider's
 * endpoint cannot mark another provider dirty. It is re-seeded whenever the saved
 * provider changes, which is what discards the key box after a successful save.
 */
function ProviderPanel({ provider, isDefault, busy, onSave, onRemoveKey }: ProviderPanelProps) {
  const [settings, setSettings] = useState<Record<string, string>>(provider.settings);
  const [key, setKey] = useState('');
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    setSettings(provider.settings);
    setKey('');
    setEntering(false);
  }, [provider]);

  const dirty =
    key.trim() !== '' ||
    provider.fields.some((f) => (settings[f.key] ?? '') !== (provider.settings[f.key] ?? ''));

  const keySetWhen = formatWhen(provider.api_key_set_at);

  return (
    <Box sx={{ px: 2.5, py: 2.25, borderBottom: 1, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">{provider.name}</Typography>
        <StatusChip
          tone={provider.configured ? 'success' : 'neutral'}
          label={provider.configured ? 'Configured' : 'Not configured'}
        />
        {isDefault ? <StatusChip tone="info" label="Application default" /> : null}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        {provider.description}
      </Typography>

      <Stack spacing={1.75}>
        {provider.fields.map((field) => (
          <TextField
            key={field.key}
            size="small"
            label={field.label}
            placeholder={field.placeholder}
            helperText={field.helperText}
            value={settings[field.key] ?? ''}
            onChange={(e) => setSettings({ ...settings, [field.key]: e.target.value })}
            fullWidth
          />
        ))}

        {/*
          THE KEY, AS A STATE AND NOT A VALUE.

          There is nothing to show, because the server returns nothing to show. So:
          a chip saying set or not set, the last four characters to tell one key from
          another, and a control to put a new one in.
        */}
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <StatusChip
              tone={provider.has_api_key ? 'success' : 'neutral'}
              label={
                provider.has_api_key
                  ? `${provider.keyLabel} stored${provider.api_key_hint ? ` · …${provider.api_key_hint}` : ''}`
                  : `No ${provider.keyLabel.toLowerCase()} stored`
              }
            />
            {!entering ? (
              <Button size="small" onClick={() => setEntering(true)} disabled={busy}>
                {provider.has_api_key ? 'Replace key' : 'Add key'}
              </Button>
            ) : null}
            {provider.has_api_key && !entering ? (
              <Button size="small" color="error" onClick={onRemoveKey} disabled={busy}>
                Remove key
              </Button>
            ) : null}
          </Stack>

          {provider.has_api_key && keySetWhen ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              Set {keySetWhen}
              {provider.api_key_set_by ? ` by ${provider.api_key_set_by}` : ''}
            </Typography>
          ) : null}

          {entering ? (
            <TextField
              size="small"
              type="password"
              label={provider.keyLabel}
              value={key}
              onChange={(e) => setKey(e.target.value)}
              helperText={`${provider.keyHelperText} Encrypted at rest with AES-256-GCM and never shown again.`}
              fullWidth
              autoComplete="off"
              sx={{ mt: 1.25 }}
            />
          ) : null}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {/* One filled button per section — the action the section exists for. */}
          <Button
            variant="contained"
            size="small"
            disabled={busy || !dirty}
            onClick={() =>
              onSave({
                settings: Object.fromEntries(provider.fields.map((f) => [f.key, settings[f.key] ?? ''])),
                ...(key.trim() ? { apiKey: key.trim() } : {}),
              })
            }
          >
            Save {provider.name}
          </Button>
          {entering ? (
            <Button
              size="small"
              disabled={busy}
              onClick={() => {
                setKey('');
                setEntering(false);
              }}
            >
              Cancel
            </Button>
          ) : null}
          {/*
            `updated_by`, not `updated_at`, decides whether this caption appears.
            Migration 0104 seeds a row per provider with `updated_at NOT NULL DEFAULT
            CURRENT_TIMESTAMP`, so a provider nobody has ever touched still carries a
            timestamp — keying off it would have every provider claiming a save that
            never happened. `updated_by` is null until a person writes one.
          */}
          {provider.updated_by && provider.updated_at && !dirty ? (
            <Typography variant="caption" color="text.secondary">
              Saved {formatWhen(provider.updated_at)} by {provider.updated_by}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

export function OpenAiIntegrationTab() {
  const { data, loading, error, reload } = useAsyncData<AiIntegrationConfig>(() =>
    api.admin.getAiIntegrations(),
  );
  const [config, setConfig] = useState<AiIntegrationConfig>();
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [saved, setSaved] = useState<string>();

  useEffect(() => setConfig(data), [data]);

  /*
    ONLY THE TWO PANELS SCROLL.

    The shell's `<main>` carries `min-height: 100vh` and then grows with its content,
    so `height: 100%` resolves against nothing and the window scrolls — banner, radio
    and all. The region is sized to what is left between where it starts and the bottom
    of the window instead, and NOTHING here is a typed constant: the top depends on the
    banner's wrapping and the radio row's, the bottom on the padding AppShell reserves
    for the anchored footer. Both are things the browser already knows.

    A CALLBACK REF, not `useRef`: this component returns a spinner while the config
    loads, so the region does not exist on first render. An effect keyed on a `useRef`
    would run once against null and silently never apply a height — the exact bug that
    made the Users screen's lock do nothing at all.
  */
  const headRef = useRef<HTMLDivElement | null>(null);
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const [bodyH, setBodyH] = useState<number | null>(null);

  useEffect(() => {
    if (!bodyEl) return;
    const main = bodyEl.closest('main');

    const measure = () => {
      const top = bodyEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(260, Math.floor(window.innerHeight - top - inset));
      // Ignore sub-pixel churn, so observing <main> cannot feed itself a resize loop.
      setBodyH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };

    measure();
    window.addEventListener('resize', measure);

    // Observe EVERYTHING above the region, not one element of it: the banner, the
    // default radio and any alert that opens between them all move its top.
    const ro = new ResizeObserver(measure);
    if (headRef.current) ro.observe(headRef.current);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [bodyEl]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!config) return null;

  /** Every write returns the whole configuration, so the screen is replaced wholesale. */
  const run = async (what: string, action: () => Promise<AiIntegrationConfig>) => {
    setBusy(true);
    setSaveError(undefined);
    setSaved(undefined);
    try {
      setConfig(await action());
      setSaved(what);
    } catch (err) {
      setSaveError(describeError(err));
      // Pull the stored state back, so the screen never shows a value that failed to save.
      reload();
    } finally {
      setBusy(false);
    }
  };

  const options = providerOptions(config);
  const defaultName = providerName(config, config.default_provider);
  const defaultProvider = config.providers.find((p) => p.provider === config.default_provider);
  const overrideCount = config.modules.filter((m) => m.provider !== null).length;

  return (
    <Box>
      {/* The fixed top-right launcher, in its established place on every screen. */}
      <HelpPanel help={OPENAI_INTEGRATION_HELP} />

      <Box ref={headRef}>
        {saveError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        ) : null}
        {saved ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {saved}
          </Alert>
        ) : null}

        <SectionCard
          title="Application default provider"
          subheader="One default for the whole application. A module follows it unless it is given its own provider below."
        >
          <RadioGroup
            row
            value={config.default_provider ?? ''}
            onChange={(e) =>
              void run('Application default provider saved.', () =>
                api.admin.setAiDefaultProvider(e.target.value || null),
              )
            }
            sx={{ gap: 2, flexWrap: 'wrap' }}
          >
            {config.providers.map((p) => (
              <FormControlLabel
                key={p.provider}
                value={p.provider}
                disabled={busy}
                control={<Radio />}
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <span>{p.name}</span>
                    {!p.configured ? <StatusChip tone="neutral" label="no key" /> : null}
                  </Stack>
                }
              />
            ))}
            {/* The fourth state a radio group needs: no default chosen at all. */}
            <FormControlLabel
              value=""
              disabled={busy}
              control={<Radio />}
              label="No default chosen"
            />
          </RadioGroup>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {defaultName
              ? `${defaultName} is the application default${
                  defaultProvider && !defaultProvider.configured
                    ? ' — its connection details are incomplete.'
                    : '.'
                }`
              : 'No application default is set, so a module without its own provider has none.'}
            {/* Same reason as the provider caption: the seeded row has a timestamp. */}
            {config.updated_by && config.updated_at
              ? ` Last changed ${formatWhen(config.updated_at)} by ${config.updated_by}.`
              : ''}
          </Typography>
        </SectionCard>
      </Box>

      <Box
        ref={setBodyEl}
        sx={{
          mt: 2,
          ...(bodyH ? { height: bodyH } : {}),
          /*
            `minHeight: 0` on every item in the chain, and `overflow: hidden` here.
            A grid item defaults to `min-height: auto` and REFUSES to shrink below its
            content, so without these the panels grow the document and the page scrolls
            again — the measured height alone sizes this box and not what overflows it.
          */
          minHeight: 0,
          overflow: 'hidden',
          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          // Narrow: one column, two rows that split the height, so each panel keeps
          // its OWN scroll rather than nesting one scroller inside another.
          '@media (max-width: 1200px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: 'minmax(0, 1fr) minmax(0, 1fr)',
          },
        }}
      >
        <SectionCard
          fill
          title="Providers"
          subheader="Connection details and the API key for each provider. A key is write-only: it is encrypted on save and never shown again."
        >
          <Box sx={{ minHeight: 0 }}>
            {config.providers.map((p) => (
              <ProviderPanel
                key={p.provider}
                provider={p}
                isDefault={p.provider === config.default_provider}
                busy={busy}
                onSave={async (update) => {
                  await run(`${p.name} saved.`, () => api.admin.updateAiProvider(p.provider, update));
                }}
                onRemoveKey={async () => {
                  await run(`${p.name} key removed.`, () =>
                    api.admin.updateAiProvider(p.provider, { clearApiKey: true }),
                  );
                }}
              />
            ))}
          </Box>
        </SectionCard>

        <SectionCard
          fill
          disableContentPadding
          title="Provider by module"
          subheader={
            overrideCount === 0
              ? 'Every module follows the application default. Give one its own provider to run it somewhere else.'
              : `${overrideCount} of ${config.modules.length} modules run on their own provider; the rest follow the application default.`
          }
        >
          <Box sx={{ overflowX: 'auto', overflowY: 'auto', minHeight: 0, height: '100%' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...TH, ...STICKY_TH }}>Module</th>
                  <th style={{ ...TH, ...STICKY_TH }}>AI provider</th>
                  <th style={{ ...TH, ...STICKY_TH }}>In force</th>
                </tr>
              </thead>
              <tbody>
                {config.modules.map((m) => {
                  const inForce = m.provider ?? config.default_provider;
                  return (
                    <tr key={m.module_key}>
                      <td style={TD}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {m.name}
                        </Typography>
                        {m.updated_at ? (
                          <Typography variant="caption" color="text.secondary">
                            Changed {formatWhen(m.updated_at)}
                            {m.updated_by ? ` by ${m.updated_by}` : ''}
                          </Typography>
                        ) : null}
                      </td>
                      <td style={TD}>
                        {/*
                          Empty string is "use the application default", and it is the
                          default selection because NO ROW is stored for it. Choosing it
                          deletes the override rather than writing the default's name in,
                          so changing the default above moves this module with it.
                        */}
                        <TextField
                          size="small"
                          select
                          value={m.provider ?? ''}
                          disabled={busy}
                          onChange={(e) =>
                            void run(`${m.name} updated.`, () =>
                              api.admin.setAiModuleProvider(m.module_key, e.target.value || null),
                            )
                          }
                          sx={{ width: 260 }}
                          SelectProps={{ displayEmpty: true }}
                        >
                          <MenuItem value="">Use application default</MenuItem>
                          {options.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </td>
                      <td style={TD}>
                        {inForce ? (
                          <StatusChip
                            tone={m.provider ? 'info' : 'neutral'}
                            label={providerName(config, inForce) ?? inForce}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            None — no application default is set
                          </Typography>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        </SectionCard>
      </Box>
    </Box>
  );
}
